import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, clientsAPI, fabricsAPI, trimsAPI, measurementsAPI, settingsAPI, referralRewardsAPI, usersAPI, loyaltyAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, Search, Plus, X, User, Scissors,
  ShoppingBag, IndianRupee, Package, Calendar, Trash2, ChevronDown, Zap,
  Gift, Sparkles, UserCheck, Crown, Star, Award,
} from 'lucide-react';
import { CustomisationPanel } from '@/components/CustomisationPanel';
import { VisualConfigurator } from '@/components/configurator/VisualConfigurator';
import { VendorScoreBadge } from '@/components/VendorScorecard';
import FabricPicker from '@/components/FabricPicker';
import { ConsumptionBreakdown } from '@/components/ConsumptionBreakdown';
import { useAuth } from '@/lib/auth';

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta',
  pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

const GARMENT_ICONS = {
  jacket: '\uD83E\uDDE5', trousers: '\uD83D\uDC56', shirt: '\uD83D\uDC54',
  sherwani: '\uD83D\uDC58', bandhgala: '\uD83E\uDDE5', nehru_vest: '\uD83E\uDDBA',
  kurta: '\uD83D\uDC58', pyjama: '\uD83D\uDC56', waistcoat: '\uD83E\uDDBA',
};

const DEFAULT_MAKING = {
  jacket: 3000, trousers: 1500, shirt: 1200, sherwani: 5000,
  bandhgala: 3500, nehru_vest: 2000, kurta: 1500, pyjama: 800, waistcoat: 2000,
};

const DEFAULT_CONSUMPTION = {
  jacket: 2.5, trousers: 1.5, shirt: 2.0, sherwani: 4.5,
  bandhgala: 2.5, nehru_vest: 1.2, kurta: 3.0, pyjama: 1.8, waistcoat: 1.2,
};

const CURRENCIES = ['INR', 'AED', 'USD', 'GBP'];
const CURRENCY_SYMBOLS = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };

const STEPS = [
  { num: 1, label: 'Select Client' },
  { num: 2, label: 'Add Garments' },
  { num: 3, label: 'Pricing' },
  { num: 4, label: 'Review & Confirm' },
];

// Map fabric colour names to subtle tint fills for the garment illustration
function fabricColorMap(colour) {
  if (!colour) return undefined;
  const c = colour.toLowerCase();
  const map = {
    navy: 'rgba(27,42,74,0.12)', blue: 'rgba(59,130,246,0.10)', 'light blue': 'rgba(147,197,253,0.15)',
    charcoal: 'rgba(55,65,81,0.12)', grey: 'rgba(107,114,128,0.08)', 'light grey': 'rgba(209,213,219,0.15)',
    black: 'rgba(17,24,39,0.15)', white: 'rgba(250,250,250,0.3)', cream: 'rgba(255,251,235,0.4)',
    ivory: 'rgba(255,255,240,0.3)', brown: 'rgba(146,64,14,0.10)', tan: 'rgba(180,83,9,0.08)',
    beige: 'rgba(217,119,6,0.06)', burgundy: 'rgba(157,23,77,0.10)', wine: 'rgba(157,23,77,0.08)',
    maroon: 'rgba(127,29,29,0.10)', olive: 'rgba(101,163,13,0.08)', green: 'rgba(22,163,74,0.08)',
    pink: 'rgba(244,114,182,0.10)', red: 'rgba(220,38,38,0.08)', purple: 'rgba(139,92,246,0.10)',
    gold: 'rgba(197,165,90,0.12)', silver: 'rgba(203,213,225,0.15)',
  };
  return map[c] || `rgba(27,42,74,0.06)`;
}

export default function NewOrderPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Client
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearching, setClientSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientMeasurements, setClientMeasurements] = useState([]);
  const [activeMeasurement, setActiveMeasurement] = useState(null);
  // Stylist/Salesperson
  const [salespeople, setSalespeople] = useState([]);
  const [selectedStylistId, setSelectedStylistId] = useState('');
  // Quick add
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);

  // Unredeemed rewards
  const [unredeemedRewards, setUnredeemedRewards] = useState([]);
  const [rewardToApply, setRewardToApply] = useState(null); // selected reward to apply to this order

  // Loyalty program
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loyaltyRewards, setLoyaltyRewards] = useState([]);
  const [showLoyaltyRewards, setShowLoyaltyRewards] = useState(false);
  const [loyaltyRewardToApply, setLoyaltyRewardToApply] = useState(null);

  // Step 2: Garments
  const [garments, setGarments] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [trims, setTrims] = useState([]);
  const [specDefs, setSpecDefs] = useState({});
  const [houseDefaults, setHouseDefaults] = useState({});

  // Step 3: Pricing
  const [currency, setCurrency] = useState('INR');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [requiredByDate, setRequiredByDate] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isExpress, setIsExpress] = useState(false);
  const [expressSettings, setExpressSettings] = useState(null);

  // Load fabrics, trims, customisation specs, and salespeople once
  useEffect(() => {
    fabricsAPI.list({ limit: 200 }).then(r => setFabrics(r.data?.fabrics || r.data || [])).catch(() => {});
    trimsAPI.list({ limit: 200 }).then(r => setTrims(r.data?.trims || r.data || [])).catch(() => {});
    ordersAPI.customisationSpec().then(r => {
      setSpecDefs(r.data?.specs || {});
      setHouseDefaults(r.data?.house_defaults || {});
    }).catch(() => {});
    settingsAPI.getExpressSettings().then(r => setExpressSettings(r.data)).catch(() => {});
    // Load salespeople for stylist dropdown
    usersAPI.getSalespeople().then(r => {
      const sp = r.data || [];
      setSalespeople(sp);
      // Auto-select current user as stylist
      if (user?.id) {
        setSelectedStylistId(user.id);
      }
    }).catch(() => {});
  }, [user?.id]);

  // Client search
  const searchClients = useCallback(async () => {
    if (!clientSearch.trim()) { setClients([]); return; }
    setClientSearching(true);
    try {
      const res = await clientsAPI.list({ search: clientSearch, limit: 10 });
      setClients(res.data?.clients || res.data || []);
    } catch { setClients([]); }
    setClientSearching(false);
  }, [clientSearch]);

  useEffect(() => {
    const timer = setTimeout(searchClients, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Load measurements, unredeemed rewards, and loyalty when client is selected
  useEffect(() => {
    if (!selectedClient) {
      setClientMeasurements([]);
      setActiveMeasurement(null);
      setUnredeemedRewards([]);
      setRewardToApply(null);
      setLoyaltyData(null);
      setLoyaltyRewards([]);
      setLoyaltyRewardToApply(null);
      return;
    }
    // Load measurements
    measurementsAPI.list(selectedClient.id).then(r => {
      const ms = r.data || [];
      setClientMeasurements(ms);
      const active = ms.find(m => m.is_active);
      setActiveMeasurement(active || null);
    }).catch(() => {});
    // Load unredeemed rewards
    referralRewardsAPI.getUnredeemed(selectedClient.id).then(r => {
      setUnredeemedRewards(r.data || []);
    }).catch(() => setUnredeemedRewards([]));
    // Load loyalty program
    loyaltyAPI.getProgram(selectedClient.id).then(r => {
      setLoyaltyData(r.data || null);
    }).catch(() => setLoyaltyData(null));
    // Load eligible loyalty rewards
    loyaltyAPI.getEligibleRewards(selectedClient.id).then(r => {
      setLoyaltyRewards(r.data?.eligible_rewards || []);
    }).catch(() => setLoyaltyRewards([]));
  }, [selectedClient]);

  // Quick add client
  const handleQuickAdd = async () => {
    if (!quickName.trim() || !quickPhone.trim()) return;
    setQuickAdding(true);
    try {
      const parts = quickName.trim().split(' ');
      const res = await clientsAPI.create({
        first_name: parts[0] || quickName,
        last_name: parts.slice(1).join(' ') || '',
        phone: quickPhone,
        service_mode: 'walk_in',
      });
      setSelectedClient(res.data);
      setShowQuickAdd(false);
      setQuickName('');
      setQuickPhone('');
      toast.success('Client added');
    } catch (err) {
      toast.error('Failed to add client');
    }
    setQuickAdding(false);
  };

  // Garment management
  const addGarment = (type) => {
    setGarments(prev => [...prev, {
      id: Date.now().toString(),
      garment_type: type,
      fabric_id: null,
      fabric: null,
      fabric_quantity_metres: DEFAULT_CONSUMPTION[type] || 2.0,
      measurement_set_id: activeMeasurement?.id || null,
      customisation_spec: { ...(houseDefaults[type] || {}) },
      trims_spec: [],
      making_charge: DEFAULT_MAKING[type] || 1500,
      notes: '',
    }]);
  };

  const updateGarment = (id, field, value) => {
    setGarments(prev => prev.map(g => g.id === id ? { ...g, [field]: value } : g));
  };

  const removeGarment = (id) => {
    setGarments(prev => prev.filter(g => g.id !== id));
  };

  const selectFabricForGarment = (garmentId, fabric) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId ? { ...g, fabric_id: fabric.id, fabric: fabric } : g
    ));
  };

  // Calculations
  const calculateItemCost = (g) => {
    const fabricPrice = g.fabric?.price_per_metre || 0;
    const fabricCost = g.fabric_quantity_metres * fabricPrice;
    const trimsCost = (g.trims_spec || []).reduce((s, t) => s + (t.cost || 0), 0);
    return { fabricCost: Math.round(fabricCost * 100) / 100, trimsCost, making: g.making_charge };
  };

  const calculatedTotal = garments.reduce((sum, g) => {
    const c = calculateItemCost(g);
    return sum + c.fabricCost + c.trimsCost + c.making;
  }, 0);

  // Express surcharge calculation
  const expressSurcharge = isExpress ? garments.reduce((sum, g) => {
    const pct = expressSettings?.surcharges?.[g.garment_type] ?? 25;
    return sum + Math.round(g.making_charge * pct / 100);
  }, 0) : 0;

  const daysUntilDelivery = requiredByDate
    ? Math.ceil((new Date(requiredByDate) - new Date(orderDate)) / (1000 * 60 * 60 * 24))
    : null;
  const expressThreshold = expressSettings?.express_threshold_days ?? 7;

  // Auto-detect express when date changes
  useEffect(() => {
    if (daysUntilDelivery !== null && daysUntilDelivery <= expressThreshold && daysUntilDelivery > 0) {
      setIsExpress(true);
    }
  }, [requiredByDate]);

  const baseTotal = priceOverride ? parseFloat(priceOverride) : calculatedTotal;
  const finalTotal = baseTotal + expressSurcharge;
  const cs = CURRENCY_SYMBOLS[currency] || '';

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const items = garments.map(g => ({
        garment_type: g.garment_type,
        fabric_id: g.fabric_id,
        fabric_quantity_metres: g.fabric_quantity_metres,
        measurement_set_id: g.measurement_set_id,
        customisation_spec: g.customisation_spec || {},
        trims_spec: g.trims_spec || [],
        making_charge: g.making_charge,
        notes: g.notes || null,
      }));

      const res = await ordersAPI.create({
        client_id: selectedClient.id,
        order_date: orderDate,
        required_by_date: requiredByDate || null,
        currency,
        notes: orderNotes || null,
        items,
        total_price_override: priceOverride ? parseFloat(priceOverride) : null,
        is_express: isExpress,
        express_surcharge: expressSurcharge,
        applied_reward_id: rewardToApply?.id || null,  // Include reward in order data
        stylist_id: selectedStylistId || null,  // Include stylist/salesperson
      });

      // Redeem the reward if one was applied
      if (rewardToApply) {
        try {
          await referralRewardsAPI.redeem(rewardToApply.id, res.data.id);
          toast.success(`Reward "${rewardToApply.reward_description}" redeemed!`);
        } catch (err) {
          console.error('Failed to redeem reward:', err);
        }
      }

      // Redeem loyalty reward if one was applied
      if (loyaltyRewardToApply) {
        try {
          const loyaltyRes = await loyaltyAPI.applyToOrder({
            client_id: selectedClient.id,
            reward_id: loyaltyRewardToApply.id,
            order_id: res.data.id,
          });
          toast.success(`Loyalty reward "${loyaltyRes.data.reward_name}" applied! (${loyaltyRes.data.points_spent} pts spent)`);
        } catch (err) {
          console.error('Failed to apply loyalty reward:', err);
          toast.error('Order created, but loyalty reward redemption failed');
        }
      }

      toast.success(`Order ${res.data.order_number} created!`);
      navigate(`/app/orders/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    }
    setSubmitting(false);
  };

  // Step validation
  const canProceed = () => {
    if (step === 1) return !!selectedClient;
    if (step === 2) return garments.length > 0;
    if (step === 3) return !!orderDate;
    return true;
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/app/orders')} className="flex items-center gap-1 text-xs font-medium mb-2 hover:underline" style={{ color: 'var(--ink-muted)' }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Orders
        </button>
        <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }}>New Order</h1>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-1" data-testid="wizard-step-indicator">
        {STEPS.map((s, i) => (
          <div key={s.num} className="flex items-center flex-1">
            <button
              onClick={() => s.num < step && setStep(s.num)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium w-full ${step === s.num ? '' : step > s.num ? 'cursor-pointer' : 'cursor-default opacity-50'}`}
              style={{
                backgroundColor: step === s.num ? 'rgba(27,42,74,0.06)' : 'transparent',
                color: step >= s.num ? 'var(--brand-navy)' : 'var(--ink-muted)',
              }}
              disabled={s.num > step}
              data-testid={`wizard-step-${s.num}`}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  backgroundColor: step > s.num ? 'var(--brand-navy)' : step === s.num ? 'var(--brand-gold)' : 'rgba(27,42,74,0.08)',
                  color: step >= s.num ? '#fff' : 'var(--ink-muted)',
                }}>
                {step > s.num ? <Check className="w-3.5 h-3.5" /> : s.num}
              </span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-0.5 mx-1 flex-shrink-0" style={{ backgroundColor: step > s.num ? 'var(--brand-navy)' : 'rgba(27,42,74,0.1)' }} />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Client */}
      {step === 1 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="wizard-step-1-content">
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Select Client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedClient ? (
              <>
                <div className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.04)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                      <span className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>
                        {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                        {selectedClient.first_name} {selectedClient.last_name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{selectedClient.phone}</p>
                    </div>
                    {/* Loyalty Tier Badge */}
                    {loyaltyData && (
                      <div className="ml-2 flex items-center gap-1.5" data-testid="loyalty-tier-badge">
                        <Badge className="text-[10px] px-2 py-0.5 gap-1 font-semibold capitalize" style={{
                          backgroundColor: loyaltyData.tier === 'platinum' ? '#1B2A4A' : loyaltyData.tier === 'gold' ? '#C5A55A' : loyaltyData.tier === 'silver' ? '#94A3B8' : '#92400E',
                          color: loyaltyData.tier === 'gold' ? '#1B2A4A' : 'white',
                        }}>
                          <Crown className="w-3 h-3" />
                          {loyaltyData.tier}
                        </Badge>
                        <span className="text-[10px] font-medium" style={{ color: 'var(--brand-gold)' }}>
                          {(loyaltyData.total_points || 0).toLocaleString()} pts
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {activeMeasurement && (
                      <Badge variant="outline" className="text-[10px] rounded-md" style={{ borderColor: 'rgba(16,185,129,0.3)', color: '#059669' }}>
                        Measurements: {activeMeasurement.garment_context}
                      </Badge>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                      data-testid="change-client-button">
                      Change
                    </Button>
                  </div>
                </div>
                
                {/* Stylist / Salesperson Selection */}
                <div className="flex items-center gap-4 p-4 rounded-xl border" style={{ borderColor: 'rgba(27,42,74,0.1)', backgroundColor: 'rgba(27,42,74,0.02)' }}>
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4" style={{ color: 'var(--brand-navy)' }} />
                    <Label className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Stylist / Salesperson</Label>
                  </div>
                  <Select value={selectedStylistId} onValueChange={setSelectedStylistId}>
                    <SelectTrigger className="w-[200px] h-9 rounded-lg" data-testid="stylist-select">
                      <SelectValue placeholder="Select stylist..." />
                    </SelectTrigger>
                    <SelectContent>
                      {salespeople.map(sp => (
                        <SelectItem key={sp.id} value={sp.id}>
                          {sp.name} ({sp.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Unredeemed Rewards Banner */}
                {unredeemedRewards.length > 0 && (
                  <div className="p-4 rounded-xl border-2 animate-in fade-in slide-in-from-top-2 duration-300"
                    style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.08)' }}
                    data-testid="unredeemed-rewards-banner">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'var(--brand-gold)' }}>
                        <Gift className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
                          <Sparkles className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
                          Unredeemed Referral Reward{unredeemedRewards.length > 1 ? 's' : ''}
                        </p>
                        <div className="mt-2 space-y-2">
                          {unredeemedRewards.map((reward) => (
                            <div key={reward.id} className="flex items-center justify-between p-2 rounded-lg bg-white/60">
                              <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                                  {reward.reward_description}
                                </p>
                                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                                  Earned from referral of {reward.referred_client_name}
                                </p>
                              </div>
                              <Button
                                variant={rewardToApply?.id === reward.id ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 text-xs gap-1"
                                style={rewardToApply?.id === reward.id ? { backgroundColor: 'var(--brand-gold)' } : {}}
                                onClick={() => setRewardToApply(rewardToApply?.id === reward.id ? null : reward)}
                                data-testid={`apply-reward-${reward.id}`}
                              >
                                {rewardToApply?.id === reward.id ? (
                                  <><Check className="w-3 h-3" /> Applied</>
                                ) : (
                                  <>Apply to Order</>
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] mt-2" style={{ color: 'var(--ink-muted)' }}>
                          Select a reward to apply to this order, or skip to save for later
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Loyalty Rewards Section */}
                {loyaltyData && loyaltyData.total_points > 0 && (
                  <div className="p-4 rounded-xl border animate-in fade-in slide-in-from-top-2 duration-300"
                    style={{ borderColor: loyaltyData.tier === 'platinum' ? '#1B2A4A' : loyaltyData.tier === 'gold' ? 'var(--brand-gold)' : 'rgba(27,42,74,0.15)', backgroundColor: 'rgba(27,42,74,0.03)' }}
                    data-testid="loyalty-rewards-banner">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: loyaltyData.tier === 'platinum' ? '#1B2A4A' : loyaltyData.tier === 'gold' ? 'var(--brand-gold)' : loyaltyData.tier === 'silver' ? '#94A3B8' : '#92400E' }}>
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
                            <Star className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
                            Loyalty: {(loyaltyData.total_points || 0).toLocaleString()} points
                            <span className="text-[10px] font-normal" style={{ color: 'var(--ink-muted)' }}>
                              (value: {CURRENCY_SYMBOLS[currency]}{Math.floor(loyaltyData.total_points / 10).toLocaleString()})
                            </span>
                          </p>
                          <Button
                            variant="outline" size="sm" className="h-7 text-xs gap-1"
                            onClick={() => setShowLoyaltyRewards(true)}
                            data-testid="redeem-loyalty-btn"
                          >
                            <Gift className="w-3 h-3" /> Redeem Reward
                          </Button>
                        </div>
                        {loyaltyRewardToApply && (
                          <div className="mt-2 p-2 rounded-lg flex items-center justify-between" style={{ backgroundColor: 'rgba(197,165,90,0.12)' }}>
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{loyaltyRewardToApply.name}</p>
                              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                                {loyaltyRewardToApply.reward_type === 'discount_percentage' 
                                  ? `${loyaltyRewardToApply.reward_value}% off` 
                                  : `${CURRENCY_SYMBOLS[currency]}${loyaltyRewardToApply.reward_value?.toLocaleString()} off`}
                                {' '}— {loyaltyRewardToApply.points_required?.toLocaleString()} pts
                              </p>
                            </div>
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500"
                              onClick={() => setLoyaltyRewardToApply(null)}>
                              <X className="w-3 h-3" /> Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search by name or phone..."
                    className="pl-9 h-11 rounded-lg"
                    autoFocus
                    data-testid="client-search-input"
                  />
                </div>
                {clientSearch && (
                  <div className="border rounded-lg max-h-[250px] overflow-y-auto" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
                    {clientSearching ? (
                      <p className="text-center py-4 text-sm" style={{ color: 'var(--ink-muted)' }}>Searching...</p>
                    ) : clients.length === 0 ? (
                      <p className="text-center py-4 text-sm" style={{ color: 'var(--ink-muted)' }}>No clients found</p>
                    ) : (
                      clients.map(c => (
                        <button key={c.id} onClick={() => { setSelectedClient(c); setClientSearch(''); }}
                          className="w-full text-left px-4 py-3 hover:bg-[color:var(--surface-2)] flex items-center gap-3 border-b last:border-b-0"
                          style={{ borderColor: 'rgba(27,42,74,0.05)', transition: 'background-color 120ms' }}
                          data-testid={`client-option-${c.id}`}>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                            <span className="text-xs font-bold" style={{ color: 'var(--brand-navy)' }}>
                              {c.first_name?.[0]}{c.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{c.first_name} {c.last_name}</p>
                            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{c.phone}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <Separator />
                {!showQuickAdd ? (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowQuickAdd(true)} data-testid="quick-add-client-button">
                    <Plus className="w-3.5 h-3.5" /> Quick Add Client
                  </Button>
                ) : (
                  <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: 'rgba(27,42,74,0.1)', backgroundColor: 'rgba(27,42,74,0.02)' }}>
                    <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Quick Add Client</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input value={quickName} onChange={(e) => setQuickName(e.target.value)} placeholder="Full name" data-testid="quick-add-name" />
                      <Input value={quickPhone} onChange={(e) => setQuickPhone(e.target.value)} placeholder="Phone number" data-testid="quick-add-phone" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleQuickAdd} disabled={quickAdding || !quickName.trim() || !quickPhone.trim()}
                        className="gap-1 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="quick-add-save">
                        <Check className="w-3.5 h-3.5" /> {quickAdding ? 'Adding...' : 'Add'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowQuickAdd(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Add Garments */}
      {step === 2 && (
        <div className="space-y-4" data-testid="wizard-step-2-content">
          {/* Add Garment Selector */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Add Garments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
                {Object.entries(GARMENT_LABELS).map(([type, label]) => (
                  <button key={type} onClick={() => addGarment(type)}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl border border-border/60 hover:border-[color:var(--brand-gold)] hover:bg-[rgba(197,165,90,0.04)]"
                    style={{ transition: 'border-color 150ms, background-color 150ms' }}
                    data-testid={`add-garment-${type}`}>
                    <span className="text-xl">{GARMENT_ICONS[type]}</span>
                    <span className="text-[10px] font-medium" style={{ color: 'var(--navy-ink)' }}>{label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Added Garments */}
          {garments.length === 0 ? (
            <Card className="rounded-xl border border-dashed border-border bg-white">
              <CardContent className="p-8 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium" style={{ color: 'var(--ink-muted)' }}>
                  Select garment types above to add to this order
                </p>
              </CardContent>
            </Card>
          ) : (
            garments.map((g, idx) => (
              <GarmentCard
                key={g.id}
                garment={g}
                index={idx}
                fabrics={fabrics}
                trims={trims}
                measurements={clientMeasurements}
                activeMeasurement={activeMeasurement}
                onUpdate={updateGarment}
                onRemove={removeGarment}
                onSelectFabric={selectFabricForGarment}
                currency={currency}
                cs={cs}
                calculateCost={calculateItemCost}
                specDefs={specDefs}
                houseDefaults={houseDefaults}
                requiredByDate={requiredByDate}
                clientBudgetTier={selectedClient?.style_profile?.budget_tier || selectedClient?.budget_tier}
              />
            ))
          )}

          {/* Running Subtotal */}
          {garments.length > 0 && (
            <div className="flex justify-end">
              <div className="text-right p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <p className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Running Subtotal</p>
                <p className="text-xl font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                  {cs}{calculatedTotal.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Pricing */}
      {step === 3 && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="wizard-step-3-content">
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Pricing & Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Cost Breakdown */}
            <div className="rounded-lg border p-4 space-y-2" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Cost Breakdown</p>
              {garments.map((g, i) => {
                const c = calculateItemCost(g);
                return (
                  <div key={g.id} className="flex justify-between text-sm">
                    <span style={{ color: 'var(--navy-ink)' }}>{GARMENT_LABELS[g.garment_type]} — Fabric {cs}{c.fabricCost.toLocaleString('en-IN')} + Making {cs}{c.making.toLocaleString('en-IN')}</span>
                    <span className="font-medium tabular-nums" style={{ color: 'var(--navy-ink)' }}>{cs}{(c.fabricCost + c.trimsCost + c.making).toLocaleString('en-IN')}</span>
                  </div>
                );
              })}
              <Separator />
              <div className="flex justify-between text-base font-semibold">
                <span style={{ color: 'var(--brand-navy)' }}>Calculated Total</span>
                <span className="tabular-nums" style={{ color: 'var(--brand-navy)' }}>{cs}{calculatedTotal.toLocaleString('en-IN')}</span>
              </div>
              {isExpress && expressSurcharge > 0 && (
                <>
                  <div className="flex justify-between text-sm" style={{ color: '#D97706' }}>
                    <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Express Surcharge</span>
                    <span className="font-medium tabular-nums">+{cs}{expressSurcharge.toLocaleString('en-IN')}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-base font-bold">
                    <span style={{ color: 'var(--brand-navy)' }}>Grand Total</span>
                    <span className="tabular-nums" style={{ color: 'var(--brand-navy)' }}>{cs}{finalTotal.toLocaleString('en-IN')}</span>
                  </div>
                </>
              )}
            </div>

            {/* Express Order Toggle */}
            <div className="rounded-lg border p-4" style={{ borderColor: isExpress ? 'rgba(217,119,6,0.4)' : 'rgba(27,42,74,0.1)', backgroundColor: isExpress ? 'rgba(217,119,6,0.04)' : 'transparent' }}
              data-testid="express-toggle-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" style={{ color: isExpress ? '#D97706' : 'var(--ink-muted)' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: isExpress ? '#D97706' : 'var(--navy-ink)' }}>Express / Rush Order</p>
                    {isExpress && daysUntilDelivery && (
                      <p className="text-[10px] mt-0.5" style={{ color: '#D97706' }}>
                        Express surcharge: +{cs}{expressSurcharge.toLocaleString('en-IN')} (delivery within {daysUntilDelivery} day{daysUntilDelivery !== 1 ? 's' : ''})
                      </p>
                    )}
                    {!isExpress && (
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                        Toggle on to apply express surcharge on making charges
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={() => setIsExpress(!isExpress)}
                  className="w-11 h-6 rounded-full relative transition-colors flex-shrink-0"
                  style={{ backgroundColor: isExpress ? '#D97706' : 'rgba(27,42,74,0.15)' }}
                  data-testid="express-toggle">
                  <span className="w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-sm transition-all"
                    style={{ left: isExpress ? '22px' : '2px' }} />
                </button>
              </div>
            </div>

            {/* Making charges per garment */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Making Charges</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {garments.map((g, i) => (
                  <div key={g.id} className="flex items-center gap-2">
                    <span className="text-sm flex-1" style={{ color: 'var(--navy-ink)' }}>{GARMENT_LABELS[g.garment_type]}</span>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--ink-muted)' }}>{cs}</span>
                      <Input type="number" value={g.making_charge} className="pl-7 h-9 rounded-lg text-sm"
                        onChange={(e) => updateGarment(g.id, 'making_charge', parseFloat(e.target.value) || 0)}
                        data-testid={`making-charge-${g.garment_type}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Override */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                Total Price Override <span className="font-normal">(leave blank to use calculated)</span>
              </Label>
              <div className="relative w-48 mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--ink-muted)' }}>{cs}</span>
                <Input type="number" value={priceOverride} placeholder={calculatedTotal.toString()} className="pl-7 h-10 rounded-lg"
                  onChange={(e) => setPriceOverride(e.target.value)} data-testid="price-override-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold" style={{ color: 'var(--ink-muted)' }}>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold" style={{ color: 'var(--ink-muted)' }}>Order Date</Label>
                <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)}
                  className="h-10 rounded-lg mt-1" data-testid="order-date-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold" style={{ color: 'var(--ink-muted)' }}>Required By</Label>
                <Input type="date" value={requiredByDate} onChange={(e) => setRequiredByDate(e.target.value)}
                  className="h-10 rounded-lg mt-1" data-testid="required-by-date-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
              <Textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Any special instructions or notes..."
                className="mt-1 rounded-lg" rows={3} data-testid="order-notes" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Confirm */}
      {step === 4 && (
        <div className="space-y-4" data-testid="wizard-step-4-content">
          <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Review Order</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Client */}
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <User className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                    {selectedClient?.first_name} {selectedClient?.last_name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{selectedClient?.phone}</p>
                </div>
              </div>

              {/* Garments */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--ink-muted)' }}>
                  Garments ({garments.length})
                </p>
                <div className="space-y-2">
                  {garments.map(g => {
                    const c = calculateItemCost(g);
                    const specEntries = Object.entries(g.customisation_spec || {}).filter(([k, v]) => v && k !== 'monogram_text' && k !== 'monogram_font' && k !== 'monogram_colour' && k !== 'monogram_placement' && k !== 'cuff_width');
                    return (
                      <div key={g.id} className="py-2 px-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{GARMENT_ICONS[g.garment_type]}</span>
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{GARMENT_LABELS[g.garment_type]}</p>
                              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                                {g.fabric ? `${g.fabric.internal_code || g.fabric.fabric_name || 'Fabric'} · ${g.fabric_quantity_metres}m` : 'No fabric selected'}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                            {cs}{(c.fabricCost + c.trimsCost + c.making).toLocaleString('en-IN')}
                          </span>
                        </div>
                        {specEntries.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {specEntries.map(([k, v]) => (
                              <span key={k} className="text-[9px] px-1.5 py-0.5 rounded-md"
                                style={{ backgroundColor: 'rgba(27,42,74,0.04)', color: 'var(--ink-muted)' }}>
                                {k.replace(/_/g, ' ')}: <strong style={{ color: 'var(--navy-ink)' }}>{v}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dates & Pricing */}
              {isExpress && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)' }}
                  data-testid="review-express-badge">
                  <Zap className="w-4 h-4" style={{ color: '#D97706' }} />
                  <span className="text-xs font-bold" style={{ color: '#D97706' }}>EXPRESS ORDER</span>
                  <span className="text-xs" style={{ color: '#92400E' }}>+{cs}{expressSurcharge.toLocaleString('en-IN')} surcharge</span>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>Order Date</p>
                  <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--navy-ink)' }}>{orderDate}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>Required By</p>
                  <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--navy-ink)' }}>{requiredByDate || '\u2014'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>Currency</p>
                  <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--navy-ink)' }}>{currency}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(197,165,90,0.08)' }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--brand-gold)' }}>Total</p>
                  <p className="text-lg font-bold mt-0.5 tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                    {cs}{finalTotal.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {orderNotes && (
                <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</p>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--navy-ink)' }}>{orderNotes}</p>
                </div>
              )}

              {/* Applied Reward */}
              {rewardToApply && (
                <div className="p-3 rounded-lg border-2 flex items-center justify-between"
                  style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.06)' }}
                  data-testid="review-applied-reward">
                  <div className="flex items-center gap-3">
                    <Gift className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
                    <div>
                      <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--brand-gold)' }}>Applied Referral Reward</p>
                      <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--navy-ink)' }}>{rewardToApply.reward_description}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setRewardToApply(null)} className="text-xs">
                    Remove
                  </Button>
                </div>
              )}
              
              {/* Applied Loyalty Reward */}
              {loyaltyRewardToApply && (
                <div className="p-3 rounded-lg border-2 flex items-center justify-between"
                  style={{ borderColor: loyaltyData?.tier === 'platinum' ? '#1B2A4A' : 'var(--brand-gold)', backgroundColor: 'rgba(27,42,74,0.04)' }}
                  data-testid="review-applied-loyalty-reward">
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
                    <div>
                      <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--brand-gold)' }}>Loyalty Reward</p>
                      <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--navy-ink)' }}>{loyaltyRewardToApply.name}</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        {loyaltyRewardToApply.reward_type === 'discount_percentage'
                          ? `${loyaltyRewardToApply.reward_value}% off`
                          : `${CURRENCY_SYMBOLS[currency]}${loyaltyRewardToApply.reward_value?.toLocaleString()} discount`}
                        {' '}— {loyaltyRewardToApply.points_required?.toLocaleString()} points will be deducted
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setLoyaltyRewardToApply(null)} className="text-xs">
                    Remove
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : navigate('/app/orders')}
          className="gap-1.5 rounded-lg" data-testid="wizard-back-button">
          <ArrowLeft className="w-4 h-4" /> {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        {step < 4 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}
            className="gap-1.5 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
            data-testid="wizard-next-button">
            Next <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || !canProceed()}
            className="gap-1.5 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
            data-testid="wizard-confirm-button">
            <Check className="w-4 h-4" /> {submitting ? 'Creating...' : 'Confirm Order'}
          </Button>
        )}
      </div>
      
      {/* Loyalty Rewards Catalogue Dialog */}
      <Dialog open={showLoyaltyRewards} onOpenChange={setShowLoyaltyRewards}>
        <DialogContent className="max-w-md" data-testid="loyalty-rewards-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Award className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              Redeem Loyalty Reward
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {loyaltyData && (
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Your Balance</span>
                <span className="text-lg font-bold" style={{ color: 'var(--brand-gold)' }}>
                  {(loyaltyData.total_points || 0).toLocaleString()} pts
                </span>
              </div>
            )}
            <ScrollArea className="max-h-[300px]">
              {loyaltyRewards.length === 0 ? (
                <p className="text-sm text-center py-6" style={{ color: 'var(--ink-muted)' }}>
                  No rewards available for your current balance
                </p>
              ) : (
                <div className="space-y-2">
                  {loyaltyRewards.map(reward => (
                    <div key={reward.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${loyaltyRewardToApply?.id === reward.id ? 'border-2' : ''}`}
                      style={{
                        borderColor: loyaltyRewardToApply?.id === reward.id ? 'var(--brand-gold)' : 'rgba(27,42,74,0.1)',
                        backgroundColor: loyaltyRewardToApply?.id === reward.id ? 'rgba(197,165,90,0.08)' : 'white',
                      }}
                      onClick={() => {
                        setLoyaltyRewardToApply(loyaltyRewardToApply?.id === reward.id ? null : reward);
                      }}
                      data-testid={`loyalty-reward-${reward.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{reward.name}</p>
                          <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{reward.description}</p>
                          <p className="text-xs font-semibold mt-1" style={{ color: 'var(--brand-gold)' }}>
                            {reward.reward_type === 'discount_percentage'
                              ? `${reward.reward_value}% off`
                              : `${CURRENCY_SYMBOLS[currency]}${reward.reward_value?.toLocaleString()} off`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>
                            {reward.points_required?.toLocaleString()}
                          </p>
                          <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>points</p>
                          {loyaltyRewardToApply?.id === reward.id && (
                            <Check className="w-4 h-4 mt-1 ml-auto" style={{ color: 'var(--brand-gold)' }} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowLoyaltyRewards(false)}>Cancel</Button>
              <Button
                disabled={!loyaltyRewardToApply}
                onClick={() => setShowLoyaltyRewards(false)}
                style={{ backgroundColor: 'var(--brand-gold)' }}
                data-testid="confirm-loyalty-reward-btn"
              >
                {loyaltyRewardToApply ? `Apply ${loyaltyRewardToApply.name}` : 'Select a Reward'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Garment Card sub-component
// ============================================================

function GarmentCard({ garment: g, index, fabrics, onUpdate, onRemove, onSelectFabric, cs, calculateCost, measurements, activeMeasurement, specDefs, houseDefaults, requiredByDate, clientBudgetTier }) {
  const [showFabrics, setShowFabrics] = useState(false);
  const cost = calculateCost(g);

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid={`garment-card-${g.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{GARMENT_ICONS[g.garment_type]}</span>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                {GARMENT_LABELS[g.garment_type]}
              </p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Garment #{index + 1}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
              {cs}{(cost.fabricCost + cost.trimsCost + cost.making).toLocaleString('en-IN')}
            </span>
            <Button variant="ghost" size="sm" className="w-8 h-8 p-0" onClick={() => onRemove(g.id)} data-testid={`remove-garment-${g.id}`}>
              <Trash2 className="w-4 h-4" style={{ color: 'var(--danger)' }} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Fabric Selection */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Fabric</Label>
            {g.fabric ? (
              <div className="flex items-center gap-2 mt-1 p-2 rounded-lg border" style={{ borderColor: 'rgba(197,165,90,0.3)', backgroundColor: 'rgba(197,165,90,0.04)' }}>
                {g.fabric.swatch_image_url ? (
                  <img src={g.fabric.swatch_image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${g.fabric.swatch_image_url}` : g.fabric.swatch_image_url} alt="" className="w-8 h-8 rounded object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                    <Scissors className="w-4 h-4 opacity-30" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: 'var(--navy-ink)' }}>
                    {g.fabric.internal_code || g.fabric.fabric_name}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                    {cs}{g.fabric.price_per_metre}/m
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => setShowFabrics(!showFabrics)}>
                  Change
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="w-full mt-1 gap-1 text-xs h-9 rounded-lg"
                onClick={() => setShowFabrics(!showFabrics)} data-testid={`select-fabric-${g.id}`}>
                <Scissors className="w-3.5 h-3.5" /> Select Fabric
              </Button>
            )}
            {showFabrics && (
              <div className="mt-2">
                <FabricPicker
                  fabrics={fabrics}
                  onSelect={(f) => { onSelectFabric(g.id, f); setShowFabrics(false); }}
                  cs={cs}
                  requiredByDate={requiredByDate}
                  clientBudgetTier={clientBudgetTier}
                />
              </div>
            )}
          </div>

          {/* Fabric Quantity */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Fabric Qty (m)</Label>
            <Input type="number" step="0.1" value={g.fabric_quantity_metres}
              onChange={(e) => onUpdate(g.id, 'fabric_quantity_metres', parseFloat(e.target.value) || 0)}
              className="h-9 mt-1 rounded-lg text-sm" data-testid={`fabric-qty-${g.id}`} />
            {g.fabric && (
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                Cost: {cs}{cost.fabricCost.toLocaleString('en-IN')}
              </p>
            )}
          </div>

          {/* Measurement Set */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Measurement Set</Label>
            <Select value={g.measurement_set_id || ''} onValueChange={(v) => onUpdate(g.id, 'measurement_set_id', v || null)}>
              <SelectTrigger className="h-9 mt-1 rounded-lg text-xs" data-testid={`measurement-select-${g.id}`}>
                <SelectValue placeholder="Select measurement" />
              </SelectTrigger>
              <SelectContent>
                {measurements.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.garment_context} ({m.measurement_type}) {m.is_active ? '★' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Notes</Label>
            <Input value={g.notes || ''} onChange={(e) => onUpdate(g.id, 'notes', e.target.value)}
              placeholder="Special instructions..." className="h-9 mt-1 rounded-lg text-xs" data-testid={`garment-notes-${g.id}`} />
          </div>
        </div>

        {/* Consumption Breakdown */}
        <ConsumptionBreakdown
          garmentType={g.garment_type}
          fabricId={g.fabric_id}
          measurementSetId={g.measurement_set_id}
          currentQty={g.fabric_quantity_metres}
          onQuantityChange={(qty) => onUpdate(g.id, 'fabric_quantity_metres', qty)}
          cs={cs}
          pricePerMetre={g.fabric?.price_per_metre || 0}
        />

        {/* Visual Configurator */}
        <VisualConfigurator
          garmentType={g.garment_type}
          spec={g.customisation_spec || {}}
          onChange={(newSpec) => onUpdate(g.id, 'customisation_spec', newSpec)}
          params={specDefs[g.garment_type] || []}
          defaults={houseDefaults[g.garment_type]}
          fabricColor={g.fabric?.primary_colour ? fabricColorMap(g.fabric.primary_colour) : undefined}
          fabricSwatch={g.fabric?.swatch_image_url ? (g.fabric.swatch_image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${g.fabric.swatch_image_url}` : g.fabric.swatch_image_url) : undefined}
        />
      </CardContent>
    </Card>
  );
}
